// ExperienceLayout.jsx - Shared layout for experience data display
import PropTypes from 'prop-types';
import { CheckCircle, Loader, Save } from 'lucide-react';
import { PrimaryButton } from '@shared/forms/FormStyles';
import PersonalStatementSection from './PersonalStatementSection';
import EducationSection from './EducationSection';
import ExtractedRolesSection from './ExtractedRolesSection';
import SkillsSection from './SkillsSection';
import ProjectSection from './ProjectSection';
import ValidationError from '@shared/feedback/ValidationError';

/**
 * A shared layout component for displaying and editing experience data
 * Used by both CVUpload and ExperienceManager to provide a consistent UI
 * 
 * @param {Object} props - Component props
 * @param {string|null} props.personalStatement - Personal statement text
 * @param {Array} props.education - Array of education entries
 * @param {Array} props.roles - Array of role entries
 * @param {Array} props.validatedRoles - Array of boolean values indicating if roles are validated
 * @param {Array} props.skills - Array of skill entries
 * @param {Array} props.projects - Array of project entries
 * @param {Function} props.onSavePersonalStatement - Handler for saving personal statement
 * @param {Function} props.onEditRole - Handler for editing a role
 * @param {Function} props.onValidateRole - Handler for validating a role
 * @param {Function} props.onDeleteRole - Handler for deleting a role
 * @param {Function} props.saveProfile - Handler for saving the entire profile
 * @param {Array} props.saveErrors - Array of error messages
 * @param {string|null} props.successMessage - Success message to display
 * @param {boolean} props.isSaving - Whether the profile is currently being saved
 * @param {boolean} props.showSaveButton - Whether to show the save button
 * @returns {JSX.Element} The experience layout component
 */
const ExperienceLayout = ({
  personalStatement,
  education = [],
  roles = [],
  validatedRoles = [],
  skills = [],
  projects = [],
  onSavePersonalStatement,
  onEditRole,
  onValidateRole,
  onDeleteRole,
  saveProfile,
  saveErrors = [],
  successMessage = null,
  isSaving = false,
  showSaveButton = true
}) => {
  return (
    <div className="space-y-8">
      {personalStatement !== undefined && (
        <PersonalStatementSection
          personalStatement={personalStatement}
          onSave={onSavePersonalStatement}
        />
      )}
      
      {education && education.length > 0 && (
        <EducationSection education={education} />
      )}

      {roles && roles.length > 0 && (
        <ExtractedRolesSection
          roles={roles}
          validatedRoles={validatedRoles.length > 0 ? validatedRoles : new Array(roles.length).fill(true)}
          onEditRole={onEditRole}
          onValidateRole={onValidateRole || (() => {})}
          onDeleteRole={onDeleteRole}
        />
      )}

      {skills && skills.length > 0 && (
        <SkillsSection
          skillsData={skills}
          isReadOnly={true}
        />
      )}

      {projects && projects.length > 0 && (
        <ProjectSection 
          projects={projects} 
        />
      )}

      {/* Save Section */}
      {showSaveButton && (
        <div className="mt-6 flex flex-col items-end space-y-4">
          {successMessage && (
            <div className="w-full p-4 bg-huntd-success/10 border border-huntd-success/20 rounded-lg flex items-center">
              <CheckCircle className="w-5 h-5 text-huntd-success mr-2" />
              <span className="text-huntd-success">{successMessage}</span>
            </div>
          )}
          
          {saveErrors && saveErrors.length > 0 && (
            <div className="w-full">
              <ValidationError errors={saveErrors} />
            </div>
          )}

          <PrimaryButton 
            onClick={saveProfile}
            disabled={isSaving}
            className="flex items-center"
          >
            {isSaving ? (
              <>
                <Loader className="animate-spin -ml-1 mr-2 h-5 w-5" />
                Saving Profile...
              </>
            ) : (
              <>
                <Save className="w-5 h-5 mr-2" />
                Save Profile
              </>
            )}
          </PrimaryButton>
        </div>
      )}
    </div>
  );
};

ExperienceLayout.propTypes = {
  personalStatement: PropTypes.string,
  education: PropTypes.array,
  roles: PropTypes.array,
  validatedRoles: PropTypes.array,
  skills: PropTypes.array,
  projects: PropTypes.array,
  onSavePersonalStatement: PropTypes.func,
  onEditRole: PropTypes.func,
  onValidateRole: PropTypes.func,
  onDeleteRole: PropTypes.func,
  saveProfile: PropTypes.func,
  saveErrors: PropTypes.array,
  successMessage: PropTypes.string,
  isSaving: PropTypes.bool,
  showSaveButton: PropTypes.bool
};

export default ExperienceLayout;